import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/app/AuthContext";
import { useToast } from "@/app/Toast";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/data";
import { Input, Select } from "@/components/ui/form";
import { api } from "@/lib/api";
import { cn, formatDateTime } from "@/lib/utils";

type WaitlistStatus = "WAITING" | "CONVERTED" | "CANCELLED" | "EXPIRED";

interface WaitlistRow {
  id: number;
  status: WaitlistStatus;
  version: number;
  priority: number;
  customer: { id: number; name: string; email: string | null; phone: string | null };
  location: { id: number; name: string };
  preferredResource: { id: number; name: string } | null;
  serviceMode: "STATIONARY" | "MOBILE";
  desiredDateLocal: string;
  desiredWindowStartLocal: string;
  desiredWindowEndLocal: string;
  timezone: string;
  serviceIds: number[];
  addonIds: number[];
  notes: string | null;
  joinedAt: string;
  linkedBooking: { id: number; code: string } | null;
}

interface LocationRow { id: number; name: string; }

export function WaitlistsPage() {
  const { hasRole } = useAuth();
  const { toast } = useToast();
  const [rows, setRows] = useState<WaitlistRow[]>([]);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [locationId, setLocationId] = useState("");
  const [status, setStatus] = useState<WaitlistStatus | "">("WAITING");
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const canManage = hasRole("MANAGER", "ADMIN");

  async function load() {
    const params = new URLSearchParams();
    if (locationId) params.set("locationId", locationId);
    if (status) params.set("status", status);
    if (query.trim()) params.set("q", query.trim());
    try {
      const response = await api.get<WaitlistRow[]>(`/api/v1/waitlists?${params.toString()}`);
      setRows(response);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load waitlists");
    }
  }

  useEffect(() => {
    api.get<LocationRow[]>("/api/v1/locations").then(setLocations).catch(() => undefined);
  }, []);

  useEffect(() => { void load(); }, [locationId, status]);

  async function cancel(row: WaitlistRow) {
    if (!confirm(`Cancel the waitlist entry for ${row.customer.name}?`)) return;
    const reason = prompt("Cancellation reason (optional)")?.trim() || undefined;
    try {
      await api.post(`/api/v1/waitlists/${row.id}/cancel`, { version: row.version, reason });
      toast("success", "Waitlist entry cancelled");
      await load();
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Cancellation failed");
    }
  }

  async function markConverted(row: WaitlistRow) {
    const raw = prompt("Booking ID created for this waitlist entry")?.trim();
    if (!raw) return;
    const bookingId = Number(raw);
    if (!Number.isInteger(bookingId) || bookingId <= 0) {
      toast("error", "Enter a valid booking ID");
      return;
    }
    try {
      await api.post(`/api/v1/waitlists/${row.id}/mark-converted`, { version: row.version, bookingId });
      toast("success", "Waitlist linked to booking");
      await load();
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Conversion failed");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Waitlists</h1>
          <p className="text-sm text-muted-foreground">Staff-managed requests ordered by priority, then arrival.</p>
        </div>
        <Button onClick={() => setCreating((value) => !value)}>{creating ? "Close form" : "Add waitlist entry"}</Button>
      </div>

      {creating && <CreateWaitlistForm locations={locations} onSaved={async () => { setCreating(false); await load(); }} />}

      <Card>
        <CardHeader>
          <form className="flex flex-wrap gap-2" onSubmit={(event) => { event.preventDefault(); void load(); }}>
            <Select value={locationId} onChange={(event) => setLocationId(event.target.value)} aria-label="Location">
              <option value="">All locations</option>
              {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
            </Select>
            <Select value={status} onChange={(event) => setStatus(event.target.value as WaitlistStatus | "")} aria-label="Status">
              <option value="">All statuses</option>
              {(["WAITING", "CONVERTED", "CANCELLED", "EXPIRED"] as const).map((value) => <option key={value}>{value}</option>)}
            </Select>
            <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Customer name, email, phone" className="w-full sm:min-w-64 sm:flex-1" />
            <Button type="submit" variant="outline">Filter</Button>
          </form>
        </CardHeader>
        <CardContent>
          {error && <p className="mb-3 text-sm text-destructive">{error}</p>}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Requested window</TableHead>
                  <TableHead>Location / preference</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Link className="font-medium text-primary hover:underline" to={`/customers/${row.customer.id}`}>{row.customer.name}</Link>
                      <p className="text-xs text-muted-foreground">{row.customer.phone ?? row.customer.email ?? `Customer #${row.customer.id}`}</p>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{row.desiredDateLocal}</p>
                      <p className="text-xs text-muted-foreground">{row.desiredWindowStartLocal.slice(0, 5)} to {row.desiredWindowEndLocal.slice(0, 5)} {row.timezone}</p>
                    </TableCell>
                    <TableCell>
                      <p>{row.location.name}</p>
                      <p className="text-xs text-muted-foreground">{row.preferredResource?.name ?? "Any resource"} · {row.serviceMode}</p>
                    </TableCell>
                    <TableCell className="font-mono">{row.priority}</TableCell>
                    <TableCell><Badge>{row.status}</Badge></TableCell>
                    <TableCell>{formatDateTime(row.joinedAt)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {row.linkedBooking && <Link className={cn(buttonVariants({ size: "sm", variant: "outline" }))} to={`/bookings/${row.linkedBooking.id}`}>Booking</Link>}
                        {row.status === "WAITING" && (
                          <Link
                            className={cn(buttonVariants({ size: "sm", variant: "outline" }))}
                            to={`/bookings/new?waitlistId=${row.id}&customerId=${row.customer.id}&locationId=${row.location.id}&serviceIds=${row.serviceIds.join(",")}&addonIds=${row.addonIds.join(",")}&serviceMode=${row.serviceMode}&date=${row.desiredDateLocal}&windowStart=${row.desiredWindowStartLocal.slice(0, 5)}&windowEnd=${row.desiredWindowEndLocal.slice(0, 5)}${row.preferredResource ? `&resourceId=${row.preferredResource.id}` : ""}`}
                          >Prepare booking</Link>
                        )}
                        {row.status === "WAITING" && canManage && <Button size="sm" variant="destructive" onClick={() => void cancel(row)}>Cancel</Button>}
                        {row.status === "WAITING" && canManage && <Button size="sm" onClick={() => void markConverted(row)}>Mark converted</Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">No matching waitlist entries.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CreateWaitlistForm({ locations, onSaved }: { locations: LocationRow[]; onSaved: () => void | Promise<void> }) {
  const { toast } = useToast();
  const [locationId, setLocationId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [mode, setMode] = useState<"STATIONARY" | "MOBILE">("STATIONARY");
  const [resources, setResources] = useState<Array<{ id: number; name: string; type: string }>>([]);
  const [services, setServices] = useState<Array<{ id: number; name: string }>>([]);
  const [serviceIds, setServiceIds] = useState<Set<number>>(new Set());
  const [resourceId, setResourceId] = useState("");
  const [date, setDate] = useState(() => new Date(Date.now() + 86_400_000).toISOString().slice(0, 10));
  const [windowStart, setWindowStart] = useState("08:00");
  const [windowEnd, setWindowEnd] = useState("17:00");
  const [priority, setPriority] = useState("0");
  const [notes, setNotes] = useState("");
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!locationId) { setResources([]); setServices([]); return; }
    Promise.all([
      api.get<Array<{ id: number; name: string; type: string }>>(`/api/v1/ops/resources?locationId=${locationId}`),
      api.get<Array<{ id: number; name: string }>>(`/api/v1/ops/services?locationId=${locationId}`),
    ]).then(([nextResources, nextServices]) => {
      setResources(nextResources);
      setServices(nextServices);
      setServiceIds(new Set());
      setResourceId("");
    }).catch(() => { setResources([]); setServices([]); });
  }, [locationId]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!locationId || !customerId || serviceIds.size === 0) {
      toast("error", "Location, customer ID, and at least one service are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/api/v1/waitlists", {
        locationId: Number(locationId),
        customerId: Number(customerId),
        preferredResourceId: resourceId ? Number(resourceId) : null,
        serviceMode: mode,
        desiredDateLocal: date,
        desiredWindowStartLocal: windowStart,
        desiredWindowEndLocal: windowEnd,
        serviceIds: Array.from(serviceIds),
        addonIds: [],
        priority: Number(priority),
        notes: notes || null,
        bookingTemplate: mode === "MOBILE" ? { serviceAddress: { line1: address } } : {},
      });
      toast("success", "Waitlist entry created");
      await onSaved();
    } catch (error) {
      toast("error", error instanceof Error ? error.message : "Waitlist creation failed");
    } finally {
      setSaving(false);
    }
  }

  const eligibleResources = resources.filter((resource) => mode === "MOBILE" ? resource.type === "STAFF_MEMBER" : resource.type !== "STAFF_MEMBER");
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">New staff-managed entry</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-3 md:grid-cols-3">
          <div><label className="text-sm font-medium">Location</label><Select required value={locationId} onChange={(e) => setLocationId(e.target.value)}><option value="">Select…</option>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}</Select></div>
          <div><label className="text-sm font-medium">Customer ID</label><Input required type="number" min="1" value={customerId} onChange={(e) => setCustomerId(e.target.value)} /></div>
          <div><label className="text-sm font-medium">Mode</label><Select value={mode} onChange={(e) => { setMode(e.target.value as "STATIONARY" | "MOBILE"); setResourceId(""); }}><option value="STATIONARY">In-bay</option><option value="MOBILE">Mobile</option></Select></div>
          <div><label className="text-sm font-medium">Preferred resource</label><Select value={resourceId} onChange={(e) => setResourceId(e.target.value)}><option value="">Any eligible resource</option>{eligibleResources.map((resource) => <option key={resource.id} value={resource.id}>{resource.name}</option>)}</Select></div>
          <div><label className="text-sm font-medium">Desired date</label><Input required type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div className="grid gap-2 sm:grid-cols-2"><div><label className="text-sm font-medium">From</label><Input required type="time" value={windowStart} onChange={(e) => setWindowStart(e.target.value)} /></div><div><label className="text-sm font-medium">To</label><Input required type="time" value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} /></div></div>
          <div className="md:col-span-2"><label className="text-sm font-medium">Services</label><div className="mt-1 flex flex-wrap gap-2">{services.map((service) => <label key={service.id} className="flex items-center gap-2 rounded border px-3 py-2 text-sm"><input type="checkbox" checked={serviceIds.has(service.id)} onChange={(e) => { const next = new Set(serviceIds); if (e.currentTarget.checked) next.add(service.id); else next.delete(service.id); setServiceIds(next); }} />{service.name}</label>)}</div></div>
          <div><label className="text-sm font-medium">Priority</label><Input type="number" min="-100" max="100" value={priority} onChange={(e) => setPriority(e.target.value)} /></div>
          {mode === "MOBILE" && <div className="md:col-span-3"><label className="text-sm font-medium">Service address</label><Input required value={address} onChange={(e) => setAddress(e.target.value)} /></div>}
          <div className="md:col-span-3"><label className="text-sm font-medium">Notes</label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          <div className="md:col-span-3 flex justify-end"><Button type="submit" disabled={saving}>{saving ? "Saving…" : "Add to waitlist"}</Button></div>
        </form>
      </CardContent>
    </Card>
  );
}
